import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import NonControlledModal from '../components/NonControlledModal';
import NonControlledHistoryModal from '../components/NonControlledHistoryModal';
import '../global/styles.css';

function NonControlledMedications({ userInfo, isAdmin = false, onLogout, onNavigate }) {
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    fetchRecentLogs();
  }, []);

  const fetchRecentLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('non_controlled_medications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('Error fetching recent logs:', error);
        setLoading(false);
        return;
      }

      setRecentLogs(data || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = () => {
    fetchRecentLogs();
    setShowFormModal(false);
  };

  const parseMedItems = (jsonStr) => {
    if (!jsonStr) return [];
    try {
      return JSON.parse(jsonStr);
    } catch {
      return [];
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'Patient Use Restock': return { bg: '#e3f2fd', color: '#1565c0' };
      case 'Truck Restock': return { bg: '#e8f5e9', color: '#2e7d32' };
      case 'Inventory/Audit': return { bg: '#fff3e0', color: '#e65100' };
      case 'Removing Expired Medication': return { bg: '#fce4ec', color: '#c62828' };
      case 'Cabinet Inventory Restock': return { bg: '#f3e5f5', color: '#6a1b9a' };
      default: return { bg: '#f5f5f5', color: '#333' };
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '20px' }}>
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: '20px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h1 style={{
            color: '#0E3293',
            fontSize: '32px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            EMS Station
          </h1>
          <p style={{ color: '#666', fontSize: '16px' }}>
            Non-Controlled Medications
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowFormModal(true)}
            style={{ width: '100%', padding: '15px' }}
          >
            New Submission
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => setShowHistoryModal(true)}
            style={{ width: '100%', padding: '15px' }}
          >
            View History Logs
          </button>
        </div>

        <div style={{
          background: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '15px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: '#333',
            fontSize: '20px',
            fontWeight: '600',
            marginBottom: '20px',
            paddingBottom: '10px',
            borderBottom: '2px solid #0E3293'
          }}>
            Recent Activity
          </h2>

          {loading ? (
            <p style={{ textAlign: 'center', color: '#666' }}>Loading...</p>
          ) : recentLogs.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666' }}>No submissions yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {recentLogs.map((log, index) => {
                const date = new Date(log.created_at);
                const formattedDate = date.toLocaleDateString('en-US', {
                  month: '2-digit', day: '2-digit', year: 'numeric'
                });
                const formattedTime = date.toLocaleTimeString('en-US', {
                  hour: '2-digit', minute: '2-digit'
                });
                const actionColors = getActionColor(log.action);
                const medItems = parseMedItems(log.medication_items_json);

                return (
                  <div key={index} style={{
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${actionColors.color}`
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div>
                        <span style={{
                          display: 'inline-block',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: actionColors.bg,
                          color: actionColors.color,
                          marginBottom: '6px'
                        }}>
                          {log.action}
                        </span>
                        <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#333' }}>
                          <strong>{log.first_name} {log.last_name}</strong>
                          {log.run_number && <span> | Run #: {log.run_number}</span>}
                          {log.truck_number && <span> | Truck: {log.truck_number}</span>}
                        </p>
                        {medItems.length > 0 && (
                          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666' }}>
                            {medItems.map(item => `${item.medication} x${item.amount}`).join(', ')}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{formattedDate}</p>
                        <p style={{ margin: 0, fontSize: '13px', color: '#999' }}>{formattedTime}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showFormModal && (
        <NonControlledModal
          userInfo={userInfo}
          isAdmin={isAdmin}
          onClose={() => setShowFormModal(false)}
          onSubmit={handleFormSubmit}
        />
      )}

      {showHistoryModal && (
        <NonControlledHistoryModal
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  );
}

export default NonControlledMedications;
